import { morphdomCopyPreservedAttributes } from "web-ui/morphdom/preserve-attributes"
import { morphdomShouldUpdate } from "web-ui/morphdom/should-update"
import { isMorphdomIgnored } from "web-ui/morphdom/ignore"
import * as Log from "web-ui/log"

export function buildOptions(customOptions = {}) {
  const options = {
    getNodeKey: (node) => {
      if (node) {
        let key = (node.getAttribute && node.getAttribute('id')) || node.id;

        // The combination of morphdom and tom-select require that we properly
        // match options so that the individual option value does not change
        // during a morph. If it does, and an automated test is attempting to
        // select that option, it may inadvertently select a different option.
        // Combining the parent ID with the node's value is important because
        // these keys are considered unique across the entire document.
        // - Aaron, Fri Dec 5 2025
        if (!key && node instanceof HTMLOptionElement) {
          const parentId = node.parentElement.id

          if (parentId) {
            key = `${parentId}-${node.value}`
          }
        }

        return key
      }
    },

    onBeforeElUpdated: (fromElement, toElement) => {
      morphdomCopyPreservedAttributes(fromElement, toElement)

      return morphdomShouldUpdate(fromElement, toElement)
    },

    onBeforeNodeDiscarded: (node) => {
      if (!(node instanceof HTMLElement)) {
        return true
      }

      // Do not remove elements that indicate they should be ignored
      if (isMorphdomIgnored(node)) {
        Log.debug("Morphdom - Discard skipped for ignored element", { id: node.id })

        return false
      }

      return true
    },

    onElUpdated: (element) => {
      element.dispatchEvent(new CustomEvent("morphdom:updated", { bubbles: false }))
    },

    // The browser only honors the autofocus attribute while parsing the initial
    // HTML. Nodes inserted by a morph are not parsed, so their autofocus is
    // ignored. Focusing an added node that requests autofocus restores the
    // expected behavior for content that enters the page through a morph.
    //
    // The focus is applied on the next animation frame rather than during the
    // morph. When the morph is triggered by activating a control, that control
    // is refocused after the morph completes, which would override a focus
    // applied synchronously here. Deferring to the next frame applies the focus
    // after that, so the added field wins.
    // - Aaron, Mon Jun 15 2026
    onNodeAdded: (node) => {
      if (!(node instanceof HTMLElement)) {
        return
      }

      let autofocusElement = node.querySelector("[autofocus]")

      if (!autofocusElement && node.matches("[autofocus]")) {
        autofocusElement = node
      }

      if (autofocusElement) {
        requestAnimationFrame(() => autofocusElement.focus())
      }
    },

    ...customOptions
  }

  return options
}
